import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Avatar, Button, Card, Chip, Description, Input, Label, Table, Tabs, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { PermissionBoxes } from '../admin/permissionBoxes'
import { ChipList, ChoiceChips, PickMany } from '../admin/peopleKit'
import { initials } from '../lib/initials'
import { Toggle } from '../admin/sectionKit'
import { ChoiceSelect, Find, Notice } from '../admin/settingsKit'
import { Sheet } from '../admin/Sheet'
import { api } from '../lib/api'
import { initialAllBranches, roleGivable } from '../lib/people'
import { explain } from '../lib/staffErrors'
import { standingTag } from '../lib/standing'
import { languageName, STAFF_LANGUAGES, type StaffKey, type StaffLanguage, type Vars } from '../lib/staffText'
import type { Branch, Identity, Organisation, OrgRole, Permission, Screen, StaffMember } from '../lib/types'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { Pager, usePaged } from '../ui/Pager'
import { roleLabel } from '../lib/roleNames'
import { useLanguageChoice, useStaffText } from '../ui/StaffText'
import { useSession } from '../ui/Session'
import { showToast } from '../ui/toast'
import { PersonTokens } from './accessTokens'
import '../styles/settings.css'
import '../styles/people.css'

/**
 * The people who work here: one list found by name and narrowed by role, branch
 * and "cannot sign in"; a row opens the person in a dialog. A new account's first
 * password is shown once; a role the caller cannot assign is never offered.
 */

type Filter = 'all' | 'blocked' | string

const FILTER_NAMES: Partial<Record<'all' | 'blocked', StaffKey>> = {
  all: 'people.everyone',
  blocked: 'people.filter.blocked',
}

/** "All branches" in the branch filter (the filter itself keeps ''). */
const ALL = '*'

/** Beyond this many roles the dialog offers a select instead of pills. */
const ROLE_PILLS = 8

/** Why the right password would still be refused: switched off, or the policy. */
const blocked = (p: StaffMember) => !p.active || Boolean(p.standing)

export function People({
  identity,
  onChanged,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  /** After a change to the signed-in person's own account or organisation. */
  onChanged?: () => void
}) {
  const { say } = useStaffText()
  return (
    <Chrome title={say('entry.app.people')} setup>
      <main className="page pp-page">
        <header className="page__head">
          <h1 className="page__title">{say('entry.app.people')}</h1>
        </header>
        <PeopleSection identity={identity} onChanged={onChanged} />
      </main>
    </Chrome>
  )
}

/** The People list without the page around it, for the settings shell to embed. */
export function PeopleSection({ identity, onChanged }: { identity: Identity; onChanged?: () => void }) {
  const { lang, say } = useStaffText()
  // Languages mean something only while the app offers a choice (App.tsx ENGLISH_ONLY).
  const languagesShown = useLanguageChoice() !== undefined
  const [people, setPeople] = useState<StaffMember[]>([])
  const [branches, setBranches] = useState<Branch[]>([])
  const [loaded, setLoaded] = useState(false)
  // Any failure to read the directory reads as "none": no offer that would 409.
  const [hasDirectory, setHasDirectory] = useState(false)
  // Kept raw and resolved at render, so a language switch rewords it.
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [secret, setSecret] = useState<{ who: string; password: string } | null>(null)
  const [open, setOpen] = useState<{ person: StaffMember | null } | null>(null)

  const [find, setFind] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [branchFilter, setBranchFilter] = useState('')

  const me = identity.staff
  const owner = Boolean(me.owner)
  // The owner belongs to no organisation: they look at the one chosen in the top bar,
  // or the only one there is.
  const orgContext = useSession()?.org
  const organisations: Organisation[] = owner ? (orgContext?.organisations ?? []) : []
  const onlyOne = organisations.filter((o) => o.active !== false)
  const organisation = owner ? orgContext?.organisation || (onlyOne.length === 1 ? onlyOne[0]!.id : '') : ''
  const chosen = organisations.find((o) => o.id === organisation)

  const [roles, setRoles] = useState<OrgRole[]>([])
  const [screens, setScreens] = useState<Screen[]>([])
  const [mayGive, setMayGive] = useState<Permission[]>([])

  // Nobody hands out what they do not hold (mayAssign, internal/api/staff.go).
  const assignable = roles.filter((r) => roleGivable(r, mayGive))

  const load = useCallback(async () => {
    try {
      const [staff, branchList, roleList, catalogue] = await Promise.all([
        api.staffList(organisation || undefined),
        api.branches(),
        api.roles(organisation || undefined).catch(() => ({ roles: [] })),
        api.permissionScreens(organisation || undefined).catch(() => ({ screens: [], mayGive: [] })),
      ])
      setPeople(staff.staff)
      setBranches(branchList.branches)
      setRoles(roleList.roles)
      setScreens(catalogue.screens)
      setMayGive(catalogue.mayGive)
    } catch (err) {
      setError(err)
    } finally {
      setLoaded(true)
    }
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    api
      .directory(organisation || undefined)
      .then(({ directory }) => setHasDirectory(Boolean(directory?.enabled)))
      .catch(() => setHasDirectory(false))
  }, [organisation])

  // What everybody here reads until they choose otherwise; the owner's is the chosen organisation's.
  const [orgLanguage, setOrgLanguage] = useState<StaffLanguage>(identity.organisationLanguage ?? 'en')
  useEffect(() => {
    if (owner) {
      if (chosen) setOrgLanguage(chosen.staffLanguage ?? 'en')
      return
    }
    setOrgLanguage(identity.organisationLanguage ?? 'en')
  }, [owner, chosen, identity.organisationLanguage])
  const orgLanguageShown = languagesShown && (!owner || Boolean(organisation))

  function mayEdit(person: StaffMember): boolean {
    if (person.id === me.id) return true
    // The server's own answer for rows that are not this organisation's.
    if (person.editable === false) return false
    if (owner) return true
    return person.permissions.every((p) => mayGive.includes(p))
  }

  const setToast = (said: Said) => void showToast(say(said.key, said.vars))

  /** Runs one change, toasts, reloads; returns the answer for the dialog. */
  async function act<T>(fn: () => Promise<T>, said: Said): Promise<T | undefined> {
    setError(null)
    setBusy(true)
    try {
      const result = await fn()
      setToast(said)
      await load()
      return result
    } catch (err) {
      setError(err)
      return undefined
    } finally {
      setBusy(false)
    }
  }

  // The supplier's own accounts are listed apart: nobody here's to change.
  const staff = people.filter((p) => !p.installation)
  const supplier = people.filter((p) => p.installation)
  const byName = (a: StaffMember, b: StaffMember) => a.displayName.localeCompare(b.displayName, lang)

  const matches = (p: StaffMember, fl: Filter) => fl === 'all' || (fl === 'blocked' ? blocked(p) : p.roleId === fl)
  const count = (fl: Filter) => staff.filter((p) => matches(p, fl)).length
  const f = find.trim().toLowerCase()
  const shown = staff
    .filter((p) => !f || p.displayName.toLowerCase().includes(f) || p.username.includes(f))
    .filter((p) => matches(p, filter))
    .filter((p) => !branchFilter || (p.branchIds ?? []).length === 0 || p.branchIds!.includes(branchFilter))
    .sort(byName)
  const paged = usePaged(shown, `${f}|${filter}|${branchFilter}|${organisation}`)

  // A tab for each role somebody holds, in the organisation's order; "cannot sign in" only when somebody cannot.
  const filters: Filter[] = [
    'all',
    ...roles.filter((r) => count(r.id) > 0).map((r) => r.id),
    ...(count('blocked') > 0 || filter === 'blocked' ? (['blocked'] as Filter[]) : []),
  ]
  const roleOf = (id: string) => {
    const r = roles.find((x) => x.id === id)
    return r ? roleLabel(r.roleKey, r.name, lang) : ''
  }
  const filterName = (key: Filter) =>
    FILTER_NAMES[key as 'all' | 'blocked'] ? say(FILTER_NAMES[key as 'all' | 'blocked']!) : roleOf(key) || key

  const branchesHere = owner && chosen ? branches.filter((b) => b.tenantId === chosen.id) : branches
  const clear = () => {
    setFind('')
    setFilter('all')
    setBranchFilter('')
  }

  const errorText = error === null ? '' : explain(error, lang, 'common.unreachable')
  const head = [
    say('people.col.name'),
    say('admin.people.role'),
    say('admin.section.branches'),
    say('people.col.signIn'),
    say('people.col.state'),
  ]

  return (
    <div className="pp">
      <div className="pp-head">
        <Tabs selectedKey={filter} onSelectionChange={(key) => setFilter(String(key))}>
          <Tabs.ListContainer>
            <Tabs.List aria-label={say('people.show')}>
              {filters.map((key) => (
                <Tabs.Tab key={key} id={key}>
                  {filterName(key)}
                  <span className="pp-count">{count(key)}</span>
                  <Tabs.Indicator />
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>
        <Button variant="primary" onPress={() => setOpen({ person: null })}>
          <Icon name="plus" size={16} /> {say('people.add')}
        </Button>
      </div>

      {errorText && !open && <Notice>{errorText}</Notice>}

      {secret && (
        <Card className="people-secret" role="status">
          <div className="people-secret__text">
            <strong>{say('people.secretFor', { who: secret.who })}</strong>
            <span>{say('people.secretOnce')}</span>
          </div>
          <code className="people-secret__pw">{secret.password}</code>
          <Button
            variant="secondary"
            onPress={() => {
              navigator.clipboard
                ?.writeText(secret.password)
                .then(() => setToast(saying('people.toast.copied')))
                .catch(() => undefined)
            }}
          >
            <Icon name="copy" size={16} /> {say('people.copy')}
          </Button>
          <Button variant="ghost" onPress={() => setSecret(null)}>
            {say('people.done')}
          </Button>
        </Card>
      )}

      <div className="pp-toolbar">
        <Find className="pp-find" value={find} onChange={setFind} label={say('people.find')} />
        {branchesHere.length > 1 && (
          <ChoiceSelect
            label={say('common.branch')}
            value={branchFilter || ALL}
            onChange={(v) => setBranchFilter(v === ALL ? '' : v)}
            options={[{ value: ALL, label: say('people.allBranches') }, ...branchesHere.map((b) => ({ value: b.id, label: b.name }))]}
          />
        )}
        <span className="pp-spacer" />
        {shown.length !== count(filter) && <p className="pp-shown">{say('people.shown', { n: shown.length })}</p>}
      </div>

      {(shown.length > 0 || !loaded) && (
        <PeopleTable label={say('people.list')} head={head} foot={<Pager {...paged.pager} />} people={paged.rows} me={me.id} branches={branches} onOpen={(p) => setOpen({ person: p })} />
      )}

      {loaded && shown.length === 0 && (
        <Card className="pp-empty">
          {staff.length > 0 ? (
            <>
              <Icon name="search" size={20} />
              <p>{say('people.noMatch')}</p>
              <Button variant="secondary" size="sm" onPress={clear}>
                {say('people.clearFilters')}
              </Button>
            </>
          ) : owner && !organisation ? (
            <>
              <Icon name="people" size={20} />
              <p>{say('people.chooseOrganisation')}</p>
            </>
          ) : (
            <>
              <Icon name="people" size={20} />
              <p>{say('people.nobody')}</p>
              <Button variant="primary" size="sm" onPress={() => setOpen({ person: null })}>
                <Icon name="plus" size={16} /> {say('people.add')}
              </Button>
            </>
          )}
        </Card>
      )}

      {supplier.length > 0 && filter === 'all' && !f && !branchFilter && (
        <section className="people-supplier">
          <h2 className="pp-sub">{say('people.supplier')}</h2>
          <PeopleTable
            label={say('people.supplier')}
            head={head}
            people={[...supplier].sort(byName)}
            me={me.id}
            branches={branches}
            onOpen={(p) => setOpen({ person: p })}
            opens={mayEdit}
          />
        </section>
      )}

      {orgLanguageShown && (
        <Card className="people-orglang">
          <strong id="people-orglang">{say('people.orgLanguage')}</strong>
          <ToggleButtonGroup
            aria-labelledby="people-orglang"
            size="sm"
            selectionMode="single"
            disallowEmptySelection
            isDisabled={busy}
            selectedKeys={[orgLanguage]}
            onSelectionChange={(keys) => {
              const code = STAFF_LANGUAGES.find((c) => keys.has(c))
              if (!code || code === orgLanguage) return
              void act(async () => {
                await api.setOrganisationLanguage(code, owner ? organisation || undefined : undefined)
                setOrgLanguage(code)
                onChanged?.()
              }, saying('people.toast.language', { language: languageName(code) }))
            }}
          >
            {STAFF_LANGUAGES.map((code) => (
              <ToggleButton key={code} id={code}>
                {languageName(code)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Card>
      )}

      {open && (
        <PersonSheet
          key={open.person?.id ?? 'new'}
          person={open.person}
          me={identity}
          roles={roles}
          assignable={assignable}
          screens={screens}
          mayGive={mayGive}
          readOnly={open.person !== null && !mayEdit(open.person)}
          branches={branches}
          organisations={organisations}
          organisation={chosen?.slug ?? ''}
          hasDirectory={hasDirectory}
          languagesShown={languagesShown}
          busy={busy}
          error={errorText}
          onClose={() => {
            setOpen(null)
            setError(null)
          }}
          onCreate={async (body) => {
            const created = await act(() => api.createStaff(body), saying('people.toast.added', { who: body.displayName }))
            if (!created) return
            setSecret({ who: created.staff.username, password: created.firstPassword })
            setOpen(null)
          }}
          onSave={async (person, body) => {
            const saved = await act(
              () => api.updateStaff(person.id, body),
              saying('people.toast.saved', { who: body.displayName ?? person.displayName }),
            )
            if (!saved) return
            if (person.id === me.id) onChanged?.()
            setOpen(null)
          }}
          onSetPassword={async (person, password) => {
            const done = await act(
              () => api.setStaffPassword(person.id, password).then(() => true),
              saying('people.toast.passwordSet', { who: person.username }),
            )
            if (done) setOpen(null)
          }}
          onToggle={async (person) => {
            const done = await act(
              () => api.updateStaff(person.id, { active: !person.active }),
              person.active
                ? saying('people.toast.disabled', { who: person.username })
                : saying('people.toast.enabled', { who: person.username }),
            )
            if (done) setOpen({ person: done.staff as StaffMember })
          }}
          onUnlock={async (person) => {
            const done = await act(() => api.unlockStaff(person.id), saying('people.toast.unlocked', { who: person.username }))
            if (done) setOpen({ person: done.staff as StaffMember })
          }}
          onUseDirectory={async (person) => {
            const done = await act(
              () => api.updateStaff(person.id, { signIn: 'directory' }),
              saying('people.toast.directory', { who: person.username }),
            )
            if (done) setOpen({ person: done.staff as StaffMember })
          }}
        />
      )}
    </div>
  )
}

/** A HeroUI table of people; a row opens the person when `opens` allows it. */
function PeopleTable({
  label,
  head,
  people,
  me,
  branches,
  onOpen,
  opens,
  foot,
}: {
  label: string
  head: string[]
  people: StaffMember[]
  me: string
  branches: Branch[]
  onOpen: (p: StaffMember) => void
  opens?: (p: StaffMember) => boolean
  foot?: ReactNode
}) {
  const { lang, say } = useStaffText()
  const may = (p: StaffMember) => !opens || opens(p)
  return (
    <Table className="pp-table">
      <Table.ScrollContainer>
        <Table.Content
          aria-label={label}
          onRowAction={(key) => {
            const p = people.find((x) => x.id === key)
            if (p && may(p)) onOpen(p)
          }}
        >
          <Table.Header>
            {head.map((h, i) => (
              <Table.Column key={i} isRowHeader={i === 0}>
                {h}
              </Table.Column>
            ))}
            <Table.Column className="pp-arrow">
              <span className="sr">{say('people.col.open')}</span>
            </Table.Column>
          </Table.Header>
          <Table.Body>
            {people.map((person) => {
              const fromDirectory = person.signIn === 'directory'
              const standing = standingTag(person.standing, say)
              const changed = (person.added?.length ?? 0) > 0 || (person.removed?.length ?? 0) > 0
              const byGroups = fromDirectory ? say('admin.people.setByDirectory') : undefined
              return (
                <Table.Row key={person.id} id={person.id} className={(person.active ? '' : 'pp-row--off ') + (may(person) ? 'pp-row--opens' : '')}>
                  <Table.Cell>
                    <span className="people-who">
                      <Avatar size="sm" variant="soft" color={person.active ? 'accent' : 'default'}>
                        <Avatar.Fallback>{initials(person.displayName, person.username)}</Avatar.Fallback>
                      </Avatar>
                      <span className="people-who__names">
                        <b>
                          <span>{person.displayName}</span>
                          {person.id === me && (
                            <Chip size="sm" variant="soft" color="accent">
                              {say('people.you')}
                            </Chip>
                          )}
                        </b>
                        <small>{person.username}</small>
                      </span>
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="people-role" title={byGroups}>
                      <Chip size="sm" variant="secondary">
                        {person.owner ? say('common.role.superadmin') : roleLabel(person.roleKey, person.roleName, lang)}
                      </Chip>
                      {changed && <span className="people-changed" title={say('people.changed')} aria-label={say('people.changed')} />}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span title={byGroups}>
                      <BranchChips person={person} branches={branches} />
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="people-signin">
                      {fromDirectory ? <DirectoryMark /> : <KeyMark />}
                      {fromDirectory ? say('people.directory') : person.signIn === 'sso' ? say('people.sso') : say('people.password')}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <StateChip active={person.active} standing={standing} />
                  </Table.Cell>
                  <Table.Cell className="pp-arrow">{may(person) && <Icon name="right" size={16} />}</Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      {foot && <Table.Footer>{foot}</Table.Footer>}
    </Table>
  )
}

/** Whether a person can sign in: off, held by the policy, or active. */
function StateChip({ active, standing, long }: { active: boolean; standing?: string; long?: string }) {
  const { say } = useStaffText()
  if (!active) return <Chip size="sm" variant="secondary">{say('admin.people.disabled')}</Chip>
  if (standing)
    return (
      <Chip size="sm" variant="soft" color="warning">
        {long || standing}
      </Chip>
    )
  return (
    <Chip size="sm" variant="soft" color="success">
      {say('people.active')}
    </Chip>
  )
}

/** The branches a person works in: named chips while short, then counted. */
function BranchChips({ person, branches }: { person: StaffMember; branches: Branch[] }) {
  const { say } = useStaffText()
  if (person.installation) return <span className="pk-quiet">{say('people.wholeServer')}</span>
  const ids = person.branchIds ?? []
  if (ids.length === 0) return <span className="pk-quiet">{say('admin.everyBranch')}</span>
  const names = ids.map((id) => branches.find((b) => b.id === id)?.name ?? '—')
  return <ChipList names={names} />
}

type CreateBody = Parameters<typeof api.createStaff>[0]
type UpdateBody = Parameters<typeof api.updateStaff>[1]

/**
 * One person, or a new one. The account actions act at once: a typed password,
 * or directory and disable after a one-line confirmation.
 */
function PersonSheet({
  person,
  me,
  roles: pageRoles,
  assignable: pageAssignable,
  screens: pageScreens,
  mayGive: pageMayGive,
  readOnly,
  branches,
  organisations,
  organisation: startOrganisation,
  hasDirectory,
  languagesShown,
  busy,
  error,
  onClose,
  onCreate,
  onSave,
  onSetPassword,
  onToggle,
  onUnlock,
  onUseDirectory,
}: {
  person: StaffMember | null
  me: Identity
  /** Every role, so a role this caller may no longer give still shows its name. */
  roles: OrgRole[]
  assignable: OrgRole[]
  screens: Screen[]
  mayGive: Permission[]
  readOnly: boolean
  branches: Branch[]
  /** The owner's organisations; empty for everybody else. */
  organisations: Organisation[]
  organisation: string
  hasDirectory: boolean
  languagesShown: boolean
  busy: boolean
  error: string
  onClose: () => void
  onCreate: (body: CreateBody) => void
  onSave: (person: StaffMember, body: UpdateBody) => void
  onSetPassword: (person: StaffMember, password: string) => void
  onToggle: (person: StaffMember) => void
  onUnlock: (person: StaffMember) => void
  onUseDirectory: (person: StaffMember) => void
}) {
  const { lang, say } = useStaffText()
  const isNew = person === null
  const isMe = person?.id === me.staff.id
  const owner = Boolean(me.staff.owner)
  const fromDirectory = person?.signIn === 'directory'
  const mayManage = owner || me.permissions.includes('staff.manage')

  const [name, setName] = useState(person?.displayName ?? '')
  const [username, setUsername] = useState(person?.username ?? '')
  const [userTouched, setUserTouched] = useState(!isNew)
  const [organisation, setOrganisation] = useState(startOrganisation)

  // The owner adding somebody may choose another organisation; its roles and
  // what may be given there are loaded here, and none are offered until chosen.
  const ownCatalogue = isNew && owner
  const [panel, setPanel] = useState<{ organisation: string; roles: OrgRole[]; screens: Screen[]; mayGive: Permission[] } | null>(null)
  useEffect(() => {
    if (!ownCatalogue || organisation === '') return
    let live = true
    Promise.all([
      api.roles(organisation).catch(() => ({ roles: [] as OrgRole[] })),
      api.permissionScreens(organisation).catch(() => ({ screens: [] as Screen[], mayGive: [] as Permission[] })),
    ]).then(([r, c]) => {
      if (live) setPanel({ organisation, roles: r.roles, screens: c.screens, mayGive: c.mayGive })
    })
    return () => {
      live = false
    }
  }, [ownCatalogue, organisation])
  const loaded = panel !== null && panel.organisation === organisation ? panel : null
  const roles = !ownCatalogue ? pageRoles : (loaded?.roles ?? [])
  const screens = !ownCatalogue ? pageScreens : (loaded?.screens ?? pageScreens)
  const mayGive = !ownCatalogue ? pageMayGive : (loaded?.mayGive ?? [])
  const assignable = !ownCatalogue ? pageAssignable : roles.filter((r) => roleGivable(r, mayGive))

  const [roleId, setRoleId] = useState(person?.roleId ?? (ownCatalogue && startOrganisation === '' ? '' : (pageAssignable[0]?.id ?? '')))
  const [branchIds, setBranchIds] = useState<string[]>(person?.branchIds ?? [])
  // "All branches" sends an empty list; never pre-ticked for a new person (lib/people.ts).
  const [allBranches, setAllBranches] = useState(initialAllBranches(isNew, person?.branchIds))
  // Another installation administrator: offered only to the owner, only when adding.
  const [wantsInstallation, setWantsInstallation] = useState(false)
  const [asking, setAsking] = useState<'reset' | 'toggle' | 'directory' | null>(null)
  // The person's own changes, kept apart from the role so a role switch keeps them.
  const [added, setAdded] = useState<Permission[]>(person?.added ?? [])
  const [removed, setRemoved] = useState<Permission[]>(person?.removed ?? [])

  const chosenRole = roles.find((r) => r.id === roleId)
  const belongsNowhere = isNew ? wantsInstallation : Boolean(person?.owner)
  // The locked role works everywhere; others need at least one branch or all of them.
  const forcedEverywhere = belongsNowhere || Boolean(chosenRole?.locked)
  const worksEverywhere = forcedEverywhere || allBranches
  const installation = person?.installation ?? belongsNowhere

  // The owner's branch list spans every organisation; offer only this one's.
  const chosen = organisations.find((o) => o.slug === organisation)
  const tenantId = isNew ? chosen?.id : person?.tenantId
  const branchesHere = owner && tenantId ? branches.filter((b) => b.tenantId === tenantId) : branches

  // Nobody changes their own role; a directory account's role and branches come from its groups.
  const roleFixed = readOnly || isMe || fromDirectory
  const branchesFixed = readOnly || fromDirectory

  const baseline = useMemo(() => chosenRole?.permissions ?? [], [chosenRole])
  const value = useMemo(
    () => baseline.filter((p) => !removed.includes(p)).concat(added.filter((p) => !baseline.includes(p))),
    [baseline, added, removed],
  )
  function changePermissions(next: Permission[]) {
    setAdded(next.filter((p) => !baseline.includes(p)))
    setRemoved(baseline.filter((p) => !next.includes(p)))
  }

  const usernameOk = /^[a-z0-9._-]{3,32}$/.test(username)
  const valid =
    name.trim() !== '' &&
    (!isNew || usernameOk) &&
    (belongsNowhere || worksEverywhere || branchIds.length > 0) &&
    (!isNew || belongsNowhere || !owner || organisation !== '') &&
    (!isNew || belongsNowhere || roleId !== '')

  function submit() {
    if (readOnly) return
    if (isNew) {
      if (wantsInstallation) {
        onCreate({ username: username.trim(), displayName: name.trim(), installation: true })
        return
      }
      onCreate({
        username: username.trim(),
        displayName: name.trim(),
        roleId,
        branchIds: worksEverywhere ? [] : branchIds,
        organisation: organisation || undefined,
        added: added.length > 0 ? added : undefined,
        removed: removed.length > 0 ? removed : undefined,
      })
      return
    }
    // Only what changed is sent, so the audit trail reads what was meant.
    const body: UpdateBody = {}
    if (name.trim() !== person.displayName) body.displayName = name.trim()
    if (!roleFixed && !belongsNowhere && roleId !== person.roleId) body.roleId = roleId
    const nextBranches = worksEverywhere ? [] : branchIds
    if (!branchesFixed && !sameSet(nextBranches, person.branchIds ?? [])) body.branchIds = nextBranches
    if (!belongsNowhere && !sameSet(added, person.added ?? [])) body.added = added
    if (!belongsNowhere && !sameSet(removed, person.removed ?? [])) body.removed = removed
    if (Object.keys(body).length === 0) {
      onClose()
      return
    }
    onSave(person, body)
  }

  const who = person?.username ?? ''
  const standing = person ? standingTag(person.standing, say) : undefined
  const stateLong = !person
    ? ''
    : person.standing === 'locked'
      ? say('people.lockedLong')
      : person.standing === 'dormant'
        ? say('people.asleepLong')
        : ''

  const roleOptions = roles.map((r) => ({
    value: r.id,
    label: roleLabel(r.roleKey, r.name, lang),
    // Shown so a role already held is never a mystery; disabled so it is not handed on.
    disabled: !assignable.some((a) => a.id === r.id),
  }))

  return (
    <Sheet
      wide
      title={isNew ? say('people.new') : person.displayName}
      mark={
        <Avatar color="accent" aria-hidden="true">
          <Avatar.Fallback>{initials(name)}</Avatar.Fallback>
        </Avatar>
      }
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isNew ? say('people.add') : say('people.save')}
      valid={valid}
      busy={busy}
      error={error || undefined}
      hideSubmit={readOnly}
      extra={readOnly ? say('people.readOnly') : undefined}
    >
      {person && (
        <section className="people-account" aria-label={say('people.account')}>
          <div className="people-account__facts">
            <Chip size="sm" variant="secondary">
              {fromDirectory ? <DirectoryMark /> : <KeyMark />}
              {fromDirectory ? say('people.directory') : person.signIn === 'sso' ? say('people.sso') : say('people.password')}
            </Chip>
            {languagesShown && (
              <span title={say('admin.people.language')}>
                <Chip size="sm" variant="secondary">
                  {person.language ? languageName(person.language) : say('admin.people.asOrganisation')}
                </Chip>
              </span>
            )}
            <StateChip active={person.active} standing={standing} long={stateLong} />
          </div>

          {!readOnly && !isMe && asking === null && (
            <div className="people-account__acts">
              {person.standing && person.active && (
                // No confirmation: unlocking takes nothing from anybody.
                <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => onUnlock(person)}>
                  {say('admin.people.unlock')}
                </Button>
              )}
              <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => setAsking('reset')}>
                {fromDirectory ? say('people.usePassword') : say('people.setPassword')}
              </Button>
              {!fromDirectory && !person.installation && hasDirectory && (
                <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => setAsking('directory')}>
                  {say('admin.people.useDirectory')}
                </Button>
              )}
              <Button variant={person.active ? 'danger-soft' : 'secondary'} size="sm" isDisabled={busy} onPress={() => setAsking('toggle')}>
                {person.active ? say('admin.people.disable') : say('admin.people.enable')}
              </Button>
            </div>
          )}
          {asking === 'reset' && (
            <SetPassword
              text={fromDirectory ? say('people.ask.leaveDirectory', { who }) : undefined}
              busy={busy}
              onNo={() => setAsking(null)}
              onSave={(password) => onSetPassword(person, password)}
            />
          )}
          {asking === 'directory' && (
            <Confirm
              text={say('people.ask.directory')}
              yes={say('admin.people.useDirectory')}
              busy={busy}
              onNo={() => setAsking(null)}
              onYes={() => {
                setAsking(null)
                onUseDirectory(person)
              }}
            />
          )}
          {asking === 'toggle' && (
            <Confirm
              danger={person.active}
              text={person.active ? say('people.ask.disable', { who }) : say('people.ask.enable', { who })}
              yes={person.active ? say('admin.people.disable') : say('admin.people.enable')}
              busy={busy}
              onNo={() => setAsking(null)}
              onYes={() => {
                setAsking(null)
                onToggle(person)
              }}
            />
          )}
        </section>
      )}

      <div className="pk-fields">
        <TextField
          className="pp-field"
          value={name}
          isDisabled={readOnly}
          onChange={(next) => {
            setName(next)
            if (!userTouched) setUsername(suggestUsername(next))
          }}
        >
          <Label>{say('people.fullName')}</Label>
          <Input autoComplete="off" />
        </TextField>
        <TextField
          className="pp-field"
          value={username}
          isDisabled={!isNew}
          isInvalid={isNew && username !== '' && !usernameOk}
          onChange={(next) => {
            setUserTouched(true)
            setUsername(next.toLowerCase().replace(/\s/g, ''))
          }}
        >
          <Label title={isNew ? say('people.usernameHelp') : undefined}>{say('entry.username')}</Label>
          <Input autoCapitalize="none" autoComplete="off" spellCheck={false} />
          {isNew && username !== '' && !usernameOk && <Description>{say('people.usernameHelp')}</Description>}
        </TextField>
      </div>

      {isNew && owner && (
        <Toggle
          title={say('admin.people.administersServer')}
          checked={wantsInstallation}
          onChange={(next) => {
            setWantsInstallation(next)
            setOrganisation('')
            setRoleId('')
            setBranchIds([])
          }}
        />
      )}

      {isNew && owner && !belongsNowhere && (
        <div className="field">
          <span>{say('common.organisation')}</span>
          <ChoiceSelect
            label={say('common.organisation')}
            value={organisation}
            onChange={(value) => {
              setOrganisation(value)
              // Branches, roles and the person's own changes belong to the previous organisation.
              setBranchIds([])
              setRoleId('')
              setAdded([])
              setRemoved([])
            }}
            placeholder={say('common.choose')}
            options={organisations.map((o) => ({ value: o.slug, label: o.name }))}
          />
        </div>
      )}

      {!belongsNowhere && (
        <div className="field">
          <span>{say('admin.people.role')}</span>
          {roleFixed ? (
            <div className="pk-fixed">
              <Chip variant="secondary">{roleLabel(chosenRole?.roleKey, chosenRole?.name, lang)}</Chip>
              {isMe ? (
                <small>{say('people.notOwnRole')}</small>
              ) : fromDirectory ? (
                <small>{say('people.fromDirectory')}</small>
              ) : null}
            </div>
          ) : roleOptions.length > ROLE_PILLS ? (
            <ChoiceSelect label={say('admin.people.role')} value={roleId} onChange={setRoleId} placeholder={say('common.choose')} options={roleOptions} />
          ) : (
            <ChoiceChips name="person-role" label={say('admin.people.role')} options={roleOptions} value={roleId} onChange={setRoleId} />
          )}
        </div>
      )}

      <div className="field">
        <span>{say('admin.section.branches')}</span>
        {forcedEverywhere ? (
          <div className="pk-fixed">
            <Chip variant="secondary">{installation ? say('people.wholeServer') : say('admin.everyBranch')}</Chip>
          </div>
        ) : branchesFixed ? (
          <div className="pk-fixed">
            {person && <BranchChips person={person} branches={branches} />}
            {fromDirectory && <small>{say('people.fromDirectory')}</small>}
          </div>
        ) : (
          <div className="pk-stack">
            <Toggle title={say('people.allBranches')} checked={allBranches} onChange={setAllBranches} />
            {!allBranches && (
              <PickMany
                placeholder={say('people.pickBranches')}
                value={branchIds}
                onChange={setBranchIds}
                options={branchesHere.map((b) => ({
                  value: b.id,
                  label: b.name,
                  note: b.active ? b.code : say('common.retired'),
                }))}
              />
            )}
            {!allBranches && branchIds.length === 0 && <small className="pk-warn">{say('people.chooseBranch')}</small>}
          </div>
        )}
      </div>

      {!belongsNowhere && (
        <div className="field">
          <span className="pk-label-row">
            {say('people.permissions')}
            {!readOnly && (added.length > 0 || removed.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setAdded([])
                  setRemoved([])
                }}
              >
                {say('people.resetToRole')}
              </Button>
            )}
          </span>
          <PermissionBoxes screens={screens} value={value} onChange={changePermissions} baseline={baseline} mayGive={mayGive} disabled={readOnly} />
        </div>
      )}

      {person && mayManage && <PersonTokens staffId={person.id} readOnly={readOnly} />}
    </Sheet>
  )
}

function Confirm({
  text,
  yes,
  danger,
  busy,
  onYes,
  onNo,
}: {
  text: string
  yes: string
  danger?: boolean
  busy: boolean
  onYes: () => void
  onNo: () => void
}) {
  const { say } = useStaffText()
  return (
    <div className="people-confirm" role="alertdialog" aria-label={yes}>
      <p>{text}</p>
      <Button variant="ghost" size="sm" onPress={onNo}>
        {say('common.cancel')}
      </Button>
      <Button variant={danger ? 'danger' : 'primary'} size="sm" isDisabled={busy} onPress={onYes}>
        {yes}
      </Button>
    </div>
  )
}

/** The password an administrator types for somebody else; any non-empty one. */
function SetPassword({
  text,
  busy,
  onSave,
  onNo,
}: {
  text?: string
  busy: boolean
  onSave: (password: string) => void
  onNo: () => void
}) {
  const { say } = useStaffText()
  const [password, setPassword] = useState('')
  const [shown, setShown] = useState(false)
  const save = () => {
    if (password !== '' && !busy) onSave(password)
  }
  return (
    <div className="people-setpw" role="group" aria-label={say('people.setPassword')}>
      {text && <p>{text}</p>}
      <label className="field people-setpw__field">
        <span>{say('people.newPassword')}</span>
        <span className="people-setpw__input">
          <Input
            fullWidth
            type={shown ? 'text' : 'password'}
            autoComplete="new-password"
            autoFocus
            spellCheck={false}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              // The sheet around this is a form; Enter here saves the password, not the person.
              if (e.key === 'Enter') {
                e.preventDefault()
                save()
              }
            }}
          />
          <ToggleButton
            className="people-setpw__eye"
            variant="ghost"
            size="sm"
            isIconOnly
            isSelected={shown}
            onChange={setShown}
            aria-label={shown ? say('entry.hidePassword') : say('entry.showPassword')}
          >
            <Icon name={shown ? 'eyeOff' : 'eye'} size={18} />
          </ToggleButton>
        </span>
      </label>
      <Button variant="ghost" size="sm" onPress={onNo}>
        {say('common.cancel')}
      </Button>
      <Button variant="primary" size="sm" isDisabled={busy || password === ''} onPress={save}>
        {say('common.save')}
      </Button>
    </div>
  )
}

// A first guess at a username from the name typed: "Nigar Məmmədli" becomes nigar.m.
const PLAIN: Record<string, string> = { ə: 'e', ı: 'i', ö: 'o', ü: 'u', ş: 's', ç: 'c', ğ: 'g', i̇: 'i' }
function suggestUsername(name: string): string {
  const plain = name
    .trim()
    .toLocaleLowerCase('az')
    .replace(/[əıöüşçğ]|i̇/g, (c) => PLAIN[c] ?? c)
    .replace(/[^a-z\s]/g, '')
  const [first, last] = plain.split(/\s+/)
  if (!first) return ''
  return (last ? `${first}.${last[0]}` : first).slice(0, 32)
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x))
}

/** A notice waiting to be said, in whatever language the screen is in by then. */
type Said = { key: StaffKey; vars?: Vars }

function saying(key: StaffKey, vars?: Vars): Said {
  return { key, vars }
}

function KeyMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M17 6l3 3M15 8l2 2" />
    </svg>
  )
}

function DirectoryMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 21V5l8-2v18M12 8l8 2v11M3 21h18M7 9h2M7 13h2M7 17h2M15 13h2M15 17h2" />
    </svg>
  )
}
